// Step 3: File Type & Status
import { ListChecks } from 'lucide-react';
import { useUploadWizardContext } from '../context/UploadWizardContext';
import { FileType, FileStatus } from '@types';
import { cn } from '@utils/classNames';

export const Step2FileTypeStatus: React.FC = () => {
  const { data, setData } = useUploadWizardContext();

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-2 lg:space-y-2.5 xl:space-y-3 w-full">
      <div className="text-center mb-2 sm:mb-3 md:mb-2 lg:mb-2.5 xl:mb-3">
        <h2 className="font-dela text-lg sm:text-xl md:text-lg lg:text-xl xl:text-2xl text-white mb-1 sm:mb-1.5 md:mb-1 lg:mb-1">Type & Status</h2>
        <p className="text-white/60 font-inter text-sm sm:text-base md:text-xs lg:text-sm xl:text-base">Choose the file type and status</p>
      </div>

      {/* File Type Selection */}
      <div>
        <label className="block mb-2 sm:mb-2.5 md:mb-2 lg:mb-2 font-codec font-bold text-sm sm:text-base md:text-xs lg:text-sm xl:text-base text-white">File Type</label>
        <div className="flex flex-wrap gap-2 sm:gap-2.5 md:gap-1.5 lg:gap-2">
          {Object.values(FileType).map((type) => (
            <button
              key={type}
              onClick={() => setData({ fileType: type })}
              className={cn(
                'px-3 sm:px-4 md:px-3 lg:px-3.5 xl:px-4 py-2 sm:py-2.5 md:py-2 lg:py-2 rounded-lg lg:rounded-xl font-codec font-bold text-sm sm:text-base md:text-xs lg:text-sm xl:text-base transition-all',
                data.fileType === type
                  ? 'bg-primary text-black shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
              )}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Status Selection */}
      <div>
        <label className="block mb-2 sm:mb-2.5 md:mb-2 lg:mb-2 font-codec font-bold text-sm sm:text-base md:text-xs lg:text-sm xl:text-base text-white flex items-center gap-1.5 sm:gap-2 md:gap-1">
          <ListChecks className="w-4 h-4 sm:w-5 sm:h-5 md:w-3 md:h-3 lg:w-3.5 lg:h-3.5" />
          Status
        </label>
        <div className="flex flex-wrap gap-2 sm:gap-2.5 md:gap-1.5 lg:gap-2">
          <button
            onClick={() => setData({ fileStatus: FileStatus.WIP })}
            className={cn(
              'px-3 sm:px-4 md:px-3 lg:px-3.5 xl:px-4 py-1.5 sm:py-2 md:py-1.5 lg:py-2 rounded-lg lg:rounded-xl text-sm sm:text-base md:text-xs lg:text-sm xl:text-base font-inter font-semibold transition-all border',
              data.fileStatus === FileStatus.WIP
                ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'
            )}
          >
            🚧 WIP
          </button>
          <button
            onClick={() => setData({ fileStatus: FileStatus.DRAFT })}
            className={cn(
              'px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-1.5 sm:py-2 md:py-2.5 lg:py-3 xl:py-4 rounded-lg lg:rounded-xl text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-inter font-semibold transition-all border',
              data.fileStatus === FileStatus.DRAFT
                ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'
            )}
          >
            🟡 Draft
          </button>
          <button
            onClick={() => setData({ fileStatus: FileStatus.TAGGED })}
            className={cn(
              'px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-1.5 sm:py-2 md:py-2.5 lg:py-3 xl:py-4 rounded-lg lg:rounded-xl text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-inter font-semibold transition-all border',
              data.fileStatus === FileStatus.TAGGED
                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'
            )}
          >
            🏷️ Tagged
          </button>
          <button
            onClick={() => setData({ fileStatus: FileStatus.UNTAGGED })}
            className={cn(
              'px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-1.5 sm:py-2 md:py-2.5 lg:py-3 xl:py-4 rounded-lg lg:rounded-xl text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-inter font-semibold transition-all border',
              data.fileStatus === FileStatus.UNTAGGED
                ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'
            )}
          >
            ✨ Clean
          </button>
          <button
            onClick={() => setData({ fileStatus: FileStatus.READY })}
            className={cn(
              'px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-1.5 sm:py-2 md:py-2.5 lg:py-3 xl:py-4 rounded-lg lg:rounded-xl text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-inter font-semibold transition-all border',
              data.fileStatus === FileStatus.READY
                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'
            )}
          >
            🟢 Ready
          </button>
        </div>
      </div>

      <p className="text-xs sm:text-sm text-white/40 font-inter">
        Version types are set per file in the grouping step.
      </p>

    </div>
  );
};

